import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sprout, Wrench, TreePine, Home, Flower2, Palette, Package, ExternalLink, Search, Filter, Star, CheckCircle2, TrendingUp, Users, Award, Plus, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import SchemaMarkup from '@/components/SchemaMarkup';

const GardenHub = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('featured');

  const categories = [
    { id: 'all', name: 'All Categories', icon: Package },
    { id: 'seeds', name: 'Seeds & Starts', icon: Sprout },
    { id: 'tools', name: 'Tools & Equipment', icon: Wrench },
    { id: 'plants', name: 'Plants & Trees', icon: TreePine },
    { id: 'indoor', name: 'Indoor Systems', icon: Home },
    { id: 'succulents', name: 'Succulents', icon: Flower2 },
    { id: 'decor', name: 'Garden Decor', icon: Palette }
  ];

  const partners = [
    {
      id: 1,
      name: 'Seeds Now',
      category: 'seeds',
      logo: 'https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=400&q=80',
      image: 'https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=800&q=80',
      description: 'Premium heirloom and organic seeds for home gardeners. Over 3,000 varieties with guaranteed germination rates.',
      commission: '25%',
      cookie: '365 days',
      rating: 4.9,
      reviews: 2847,
      verified: true,
      featured: true,
      since: '2015',
      products: '3000+',
      highlights: ['Organic Certified', 'Heirloom Seeds', 'Free Shipping'],
      url: '#'
    },
    {
      id: 2,
      name: 'Hoss Tools',
      category: 'tools',
      logo: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400&q=80',
      image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&q=80',
      description: 'Professional-grade gardening tools and equipment. Built to last with lifetime warranties on select items.',
      commission: '10%',
      cookie: '60 days',
      rating: 4.8,
      reviews: 1923,
      verified: true,
      featured: true,
      since: '2009',
      products: '500+',
      highlights: ['Lifetime Warranty', 'Made in USA', 'Pro-Grade'],
      url: '#'
    },
    {
      id: 3,
      name: 'Nature Hills Nursery',
      category: 'plants',
      logo: 'https://images.unsplash.com/photo-1591857177580-dc82b9ac4e1e?w=400&q=80',
      image: 'https://images.unsplash.com/photo-1591857177580-dc82b9ac4e1e?w=800&q=80',
      description: 'Americas largest online plant nursery. Live plants, trees, and shrubs shipped directly to your door.',
      commission: '12%',
      cookie: '30 days',
      rating: 4.7,
      reviews: 3421,
      verified: true,
      featured: true,
      since: '2002',
      products: '5000+',
      highlights: ['Live Arrival Guarantee', 'Expert Support', 'Fast Shipping'],
      url: '#'
    },
    {
      id: 4,
      name: 'AeroGarden',
      category: 'indoor',
      logo: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=400&q=80',
      image: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=800&q=80',
      description: 'Leading hydroponic indoor gardening systems. Grow fresh herbs, vegetables, and flowers year-round.',
      commission: '10%',
      cookie: '30 days',
      rating: 4.8,
      reviews: 1567,
      verified: true,
      featured: true,
      since: '2006',
      products: '50+',
      highlights: ['NASA Technology', 'No Soil Needed', '100% Guarantee'],
      url: '#'
    },
    {
      id: 5,
      name: 'Succulents Box',
      category: 'succulents',
      logo: 'https://images.unsplash.com/photo-1459156212016-c812468e2115?w=400&q=80',
      image: 'https://images.unsplash.com/photo-1459156212016-c812468e2115?w=800&q=80',
      description: 'Monthly succulent subscription boxes and premium succulent collections. Perfect for gifts and collectors.',
      commission: '10%',
      cookie: '30 days',
      rating: 4.6,
      reviews: 892,
      verified: true,
      featured: false,
      since: '2017',
      products: '200+',
      highlights: ['Subscription Box', 'Gift-Ready', 'Rare Varieties'],
      url: '#'
    },
    {
      id: 6,
      name: 'Botanical Interests',
      category: 'seeds',
      logo: 'https://images.unsplash.com/photo-1523348837708-15d4a09cfac2?w=400&q=80',
      image: 'https://images.unsplash.com/photo-1523348837708-15d4a09cfac2?w=800&q=80',
      description: 'Family-owned seed company specializing in organic, heirloom, and unique varieties with detailed growing instructions.',
      commission: '5-15%',
      cookie: '30 days',
      rating: 4.7,
      reviews: 1234,
      verified: true,
      featured: false,
      since: '1995',
      products: '600+',
      highlights: ['Organic Options', 'Detailed Guides', 'Family-Owned'],
      url: '#'
    },
    {
      id: 7,
      name: 'West Coast Seeds',
      category: 'seeds',
      logo: 'https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?w=400&q=80',
      image: 'https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?w=800&q=80',
      description: 'Canadian seed specialists offering over 900 varieties of untreated, non-GMO seeds. Expert growing guides and superior germination rates.',
      commission: '8%',
      cookie: '30 days',
      rating: 4.8,
      reviews: 2156,
      verified: true,
      featured: true,
      since: '1983',
      products: '900+',
      highlights: ['Non-GMO', 'Canadian Company', 'Expert Guides'],
      url: 'https://www.westcoastseeds.com'
    },
    {
      id: 8,
      name: 'Eartheasy',
      category: 'tools',
      logo: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400&q=80',
      image: 'https://images.unsplash.com/photo-1558293842-c0fd3db86157?w=800&q=80',
      description: 'Eco-friendly gardening tools and sustainable living supplies. Every order plants a tree. High-quality products for conscious gardeners.',
      commission: '10%',
      cookie: '90 days',
      rating: 4.7,
      reviews: 1876,
      verified: true,
      featured: true,
      since: '2000',
      products: '1500+',
      highlights: ['Eco-Friendly', 'Plants Trees', '$600 Avg Order'],
      url: 'https://eartheasy.com'
    },
    {
      id: 9,
      name: 'Lively Root',
      category: 'plants',
      logo: 'https://images.unsplash.com/photo-1459156212016-c812468e2115?w=400&q=80',
      image: 'https://images.unsplash.com/photo-1463936575829-25148e1db1b8?w=800&q=80',
      description: 'Premium indoor and outdoor plants delivered to your door. Each plant comes with care instructions and a happiness guarantee.',
      commission: '20%',
      cookie: '30 days',
      rating: 4.8,
      reviews: 2341,
      verified: true,
      featured: true,
      since: '2018',
      products: '400+',
      highlights: ['20% Commission', 'Care Guides', 'Home Delivery'],
      url: 'https://livelyroot.com'
    },
    {
      id: 10,
      name: 'Garden Tower Project',
      category: 'indoor',
      logo: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=400&q=80',
      image: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=800&q=80',
      description: 'Revolutionary vertical self-watering garden towers. Grow 50+ plants in 4 sq ft. Perfect for patios, balconies, and small spaces.',
      commission: '15%',
      cookie: '60 days',
      rating: 4.9,
      reviews: 1234,
      verified: true,
      featured: true,
      since: '2014',
      products: '20+',
      highlights: ['Space-Saving', 'Self-Watering', '50+ Plants'],
      url: 'https://www.gardentowerproject.com'
    },
    {
      id: 11,
      name: 'Click & Grow',
      category: 'indoor',
      logo: 'https://images.unsplash.com/photo-1508140928420-7b7302f98114?w=400&q=80',
      image: 'https://images.unsplash.com/photo-1508140928420-7b7302f98114?w=800&q=80',
      description: 'Smart indoor gardens with NASA-inspired technology. No green thumb required - automated watering, nutrients, and LED grow lights.',
      commission: '10%',
      cookie: '45 days',
      rating: 4.7,
      reviews: 3567,
      verified: true,
      featured: true,
      since: '2009',
      products: '100+',
      highlights: ['Smart Technology', 'Automated Care', 'Beginner-Friendly'],
      url: 'https://www.clickandgrow.com'
    },
    {
      id: 12,
      name: 'Gardeners Supply Company',
      category: 'tools',
      logo: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400&q=80',
      image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&q=80',
      description: 'Employee-owned company with 40+ years of gardening expertise. Raised beds, planters, tools, and innovative garden solutions.',
      commission: '8%',
      cookie: '30 days',
      rating: 4.8,
      reviews: 4521,
      verified: true,
      featured: true,
      since: '1983',
      products: '8000+',
      highlights: ['Employee-Owned', '40+ Years', 'Innovative Products'],
      url: 'https://www.gardeners.com'
    },
    {
      id: 13,
      name: 'Plant Addicts',
      category: 'plants',
      logo: 'https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=400&q=80',
      image: 'https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=800&q=80',
      description: 'Unique and hard-to-find plants, flowers, and shrubs. Expert guidance for collectors and enthusiasts. Satisfaction guaranteed.',
      commission: '10%',
      cookie: '30 days',
      rating: 4.6,
      reviews: 1876,
      verified: true,
      featured: false,
      since: '2016',
      products: '2500+',
      highlights: ['Rare Plants', 'Expert Support', 'Satisfaction Guarantee'],
      url: 'https://plantaddicts.com'
    },
    {
      id: 14,
      name: 'Gardening Direct',
      category: 'plants',
      logo: 'https://images.unsplash.com/photo-1591857177580-dc82b9ac4e1e?w=400&q=80',
      image: 'https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?w=800&q=80',
      description: 'UK-based supplier of bedding plants, bulbs, shrubs, and garden supplies. Quality plants at competitive prices with expert advice.',
      commission: '10%',
      cookie: '30 days',
      rating: 4.5,
      reviews: 2134,
      verified: true,
      featured: false,
      since: '2005',
      products: '3000+',
      highlights: ['Quality Plants', 'UK Supplier', 'Competitive Prices'],
      url: 'https://www.gardeningdirect.co.uk'
    },
    {
      id: 15,
      name: 'True Leaf Market',
      category: 'seeds',
      logo: 'https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=400&q=80',
      image: 'https://images.unsplash.com/photo-1523348837708-15d4a09cfac2?w=800&q=80',
      description: 'Specialists in microgreens, sprouting seeds, and garden seeds. Premium quality with excellent germination rates.',
      commission: '15%',
      cookie: '60 days',
      rating: 4.8,
      reviews: 1567,
      verified: true,
      featured: true,
      since: '1974',
      products: '1200+',
      highlights: ['Microgreen Experts', '15% Commission', '50+ Years'],
      url: 'https://trueleafmarket.com'
    },
    {
      id: 16,
      name: 'Greenhouse Megastore',
      category: 'indoor',
      logo: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=400&q=80',
      image: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=800&q=80',
      description: 'Everything for greenhouse gardening - structures, accessories, heating, ventilation, and growing supplies.',
      commission: '8%',
      cookie: '45 days',
      rating: 4.7,
      reviews: 987,
      verified: true,
      featured: true,
      since: '1998',
      products: '5000+',
      highlights: ['Greenhouse Specialists', 'Complete Solutions', 'Expert Support'],
      url: 'https://www.greenhousemegastore.com'
    },
    {
      id: 17,
      name: 'Burpee',
      category: 'seeds',
      logo: 'https://images.unsplash.com/photo-1523348837708-15d4a09cfac2?w=400&q=80',
      image: 'https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=800&q=80',
      description: 'Americas most trusted seed brand since 1876. Award-winning vegetables, flowers, and garden supplies with proven results.',
      commission: '8%',
      cookie: '30 days',
      rating: 4.8,
      reviews: 5678,
      verified: true,
      featured: true,
      since: '1876',
      products: '4000+',
      highlights: ['Since 1876', 'Award-Winning', 'Most Trusted'],
      url: 'https://www.burpee.com'
    },
    {
      id: 18,
      name: 'Johnny\'s Selected Seeds',
      category: 'seeds',
      logo: 'https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?w=400&q=80',
      image: 'https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?w=800&q=80',
      description: 'Employee-owned company providing superior seeds, tools, and supplies for commercial and home growers. Research-based recommendations.',
      commission: '5%',
      cookie: '30 days',
      rating: 4.9,
      reviews: 3456,
      verified: true,
      featured: true,
      since: '1973',
      products: '2000+',
      highlights: ['Employee-Owned', 'Research-Based', 'Pro Quality'],
      url: 'https://www.johnnyseeds.com'
    },
    {
      id: 19,
      name: 'Terrain',
      category: 'decor',
      logo: 'https://images.unsplash.com/photo-1558904541-efa843a96f01?w=400&q=80',
      image: 'https://images.unsplash.com/photo-1558904541-efa843a96f01?w=800&q=80',
      description: 'Curated garden decor, outdoor furniture, planters, and lifestyle products. Design-forward pieces for garden enthusiasts.',
      commission: '5%',
      cookie: '14 days',
      rating: 4.6,
      reviews: 1234,
      verified: true,
      featured: false,
      since: '2008',
      products: '2500+',
      highlights: ['Design-Forward', 'Curated Selection', 'Outdoor Living'],
      url: 'https://www.shopterrain.com'
    },
    {
      id: 20,
      name: 'High Mowing Organic Seeds',
      category: 'seeds',
      logo: 'https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=400&q=80',
      image: 'https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=800&q=80',
      description: '100% certified organic, non-GMO seeds. Family-owned farm in Vermont dedicated to sustainable agriculture.',
      commission: '10%',
      cookie: '30 days',
      rating: 4.9,
      reviews: 2345,
      verified: true,
      featured: true,
      since: '1996',
      products: '700+',
      highlights: ['100% Organic', 'Family Farm', 'Non-GMO Project'],
      url: 'https://www.highmowingseeds.com'
    }
  ];

  const filteredPartners = partners
    .filter(p => selectedCategory === 'all' || p.category === selectedCategory)
    .filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                 p.description.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'featured') return b.featured - a.featured;
      if (sortBy === 'rating') return b.rating - a.rating;
      if (sortBy === 'commission') return parseFloat(b.commission) - parseFloat(a.commission);
      return 0;
    });

  return (
    <>
      <SEO
        title="Garden Hub - Premium Gardening Products Directory | Garden & Greenhouse"
        description="Discover trusted gardening partners, premium seeds, tools, plants, and supplies. Browse our curated directory of verified vendors with affiliate recommendations and expert reviews."
        keywords="garden products, seeds, garden tools, plants, greenhouse supplies, gardening equipment, affiliate garden products"
        url="https://www.gardenandgreenhouse.com/garden-hub"
      />
      <SchemaMarkup type="breadcrumb" data={{
        items: [
          {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.gardenandgreenhouse.com"},
          {"@type": "ListItem", "position": 2, "name": "Garden Hub", "item": "https://www.gardenandgreenhouse.com/garden-hub"}
        ]
      }} />
      
      <div className="min-h-screen bg-white">
      {/* Header Navigation */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-emerald-700 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Home</span>
          </Link>
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_eco-gardens-2/artifacts/8awup57n_GardenAndGreenhouse%20FINAL%20LOGO.png" 
              alt="Garden & Greenhouse" 
              className="h-10"
            />
          </Link>
        </div>
      </header>

      {/* Premium Hero */}
      <section className="relative bg-gradient-to-br from-stone-900 via-stone-800 to-emerald-900 text-white py-24">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?w=1600&q=80)', backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Award className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-semibold">Premium Partner Directory</span>
            </div>
            <h1 className="text-6xl font-bold mb-6 leading-tight">
              Garden Hub
            </h1>
            <p className="text-xl text-stone-200 mb-8 leading-relaxed">
              Discover trusted gardening partners, premium products, and exclusive deals.
              Join thousands of gardeners finding quality supplies from verified vendors.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/list-your-business">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8" data-testid="list-business-btn">
                  <Plus className="w-5 h-5 mr-2" />
                  List Your Business
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/30" data-testid="learn-more-btn">
                Learn More
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-4xl font-bold text-emerald-400 mb-2">25+</div>
              <div className="text-stone-300 text-sm">Verified Partners</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-emerald-400 mb-2">10K+</div>
              <div className="text-stone-300 text-sm">Products</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-emerald-400 mb-2">4.8</div>
              <div className="text-stone-300 text-sm">Avg Rating</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-emerald-400 mb-2">99%</div>
              <div className="text-stone-300 text-sm">Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* Search & Filter Bar */}
      <section className="sticky top-20 z-40 bg-white border-b border-stone-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Search */}
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <Input
                type="text"
                placeholder="Search partners, products, categories..."
                className="pl-10 h-12 border-stone-300"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-partners-input"
              />
            </div>

            {/* Sort */}
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-stone-600" />
              <select
                className="h-12 px-4 border border-stone-300 rounded-lg bg-white text-stone-900 font-medium"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                data-testid="sort-select"
              >
                <option value="featured">Featured</option>
                <option value="rating">Highest Rated</option>
                <option value="commission">Best Commission</option>
              </select>
            </div>
          </div>

          {/* Category Pills */}
          <div className="flex gap-2 overflow-x-auto py-4 scrollbar-hide">
            {categories.map((cat) => {
              const IconComponent = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-full font-medium whitespace-nowrap transition-all ${
                    selectedCategory === cat.id
                      ? 'bg-emerald-700 text-white shadow-md'
                      : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                  }`}
                  data-testid={`category-${cat.id}-btn`}
                >
                  <IconComponent className="w-4 h-4" />
                  <span>{cat.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partners Grid */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPartners.map((partner) => (
              <Card
                key={partner.id}
                className="group overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 border-stone-100 hover:border-emerald-500"
                data-testid={`partner-card-${partner.id}`}
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={partner.image}
                    alt={partner.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  
                  {/* Badges */}
                  <div className="absolute top-3 left-3 flex flex-col gap-2">
                    {partner.featured && (
                      <div className="bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                        FEATURED
                      </div>
                    )}
                    {partner.verified && (
                      <div className="bg-white text-emerald-700 text-xs font-semibold px-3 py-1 rounded-full shadow-lg flex items-center">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        VERIFIED
                      </div>
                    )}
                  </div>

                  {/* Rating */}
                  <div className="absolute top-3 right-3 bg-white px-3 py-1 rounded-full shadow-lg">
                    <div className="flex items-center space-x-1">
                      <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                      <span className="font-bold text-stone-900">{partner.rating}</span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-stone-900 mb-2">{partner.name}</h3>
                  <p className="text-stone-600 mb-4 line-clamp-2">{partner.description}</p>

                  {/* Highlights */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {partner.highlights.map((highlight, idx) => (
                      <span key={idx} className="bg-emerald-50 text-emerald-700 text-xs font-medium px-2 py-1 rounded">
                        {highlight}
                      </span>
                    ))}
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-4 p-4 bg-stone-50 rounded-lg">
                    <div>
                      <div className="text-xs text-stone-500 mb-1">Commission</div>
                      <div className="text-lg font-bold text-emerald-700">{partner.commission}</div>
                    </div>
                    <div>
                      <div className="text-xs text-stone-500 mb-1">Cookie</div>
                      <div className="text-lg font-bold text-stone-900">{partner.cookie}</div>
                    </div>
                    <div>
                      <div className="text-xs text-stone-500 mb-1">Products</div>
                      <div className="text-lg font-bold text-stone-900">{partner.products}</div>
                    </div>
                    <div>
                      <div className="text-xs text-stone-500 mb-1">Since</div>
                      <div className="text-lg font-bold text-stone-900">{partner.since}</div>
                    </div>
                  </div>

                  {/* Reviews */}
                  <div className="flex items-center text-sm text-stone-600 mb-4">
                    <Users className="w-4 h-4 mr-1" />
                    {partner.reviews.toLocaleString()} verified reviews
                  </div>

                  {/* CTA */}
                  <Button
                    className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold h-12 group-hover:shadow-lg transition-all"
                    data-testid={`shop-${partner.id}-btn`}
                  >
                    Visit Store
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>

          {/* No Results */}
          {filteredPartners.length === 0 && (
            <div className="text-center py-16">
              <Package className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-stone-900 mb-2">No partners found</h3>
              <p className="text-stone-600">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-green-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <TrendingUp className="w-16 h-16 mx-auto mb-6 opacity-90" />
          <h2 className="text-4xl font-bold mb-4">
            Ready to List Your Gardening Business?
          </h2>
          <p className="text-xl text-emerald-100 mb-8">
            Join our premium directory and reach thousands of gardening enthusiasts.
            Get verified, featured placement, and grow your customer base.
          </p>
          <Link to="/list-your-business">
            <Button size="lg" className="bg-white text-emerald-700 hover:bg-stone-100 font-semibold px-8" data-testid="cta-list-business-btn">
              <Plus className="w-5 h-5 mr-2" />
              List Your Business Now
            </Button>
          </Link>
        </div>
      </section>
      </div>
    </>
  );
};

export default GardenHub;
